import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { embedSrc, embedThumbnail } from '@/blocks/media';

/**
 * YouTube or Vimeo embed.
 *
 * Click-to-play, deliberately: the iframe is not created until the visitor asks
 * for it, so a page with several videos does not hand every visitor to Google or
 * Vimeo, and does not pay for three third-party players on load. YouTube is
 * loaded from youtube-nocookie.com (see lib/blocks/media.js).
 */
export default function VideoEmbed({ provider, videoId, start, poster, title, className = '', aspect = '16 / 9' }) {
    const [playing, setPlaying] = useState(false);
    const embed = { provider, id: videoId, start };
    const src = embedSrc(embed, { autoplay: true });
    const thumbnail = poster || embedThumbnail(embed);

    if (!src) return null;

    return (
        <div className={`relative w-full overflow-hidden bg-black ${className}`} style={{ aspectRatio: aspect }}>
            {playing ? (
                <iframe
                    src={src}
                    title={title || 'Video'}
                    className="absolute inset-0 w-full h-full"
                    style={{ border: 0 }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                />
            ) : (
                <button
                    type="button"
                    onClick={() => setPlaying(true)}
                    className="absolute inset-0 w-full h-full group"
                    aria-label={title ? `Play ${title}` : 'Play video'}
                >
                    {thumbnail && (
                        <img
                            src={thumbnail}
                            alt=""
                            className="absolute inset-0 w-full h-full object-cover"
                            loading="lazy"
                            decoding="async"
                        />
                    )}
                    <span className="absolute inset-0 flex items-center justify-center bg-black/25 group-hover:bg-black/35 transition-colors">
                        <span className="flex items-center justify-center w-16 h-16 rounded-full bg-white/90 shadow">
                            <svg className="w-7 h-7 ml-1" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                <path d="M8 5v14l11-7z" />
                            </svg>
                        </span>
                    </span>
                </button>
            )}
        </div>
    );
}

VideoEmbed.propTypes = {
    provider: PropTypes.string,
    videoId: PropTypes.string.isRequired,
    start: PropTypes.number,
    poster: PropTypes.string,
    title: PropTypes.string,
    className: PropTypes.string,
    aspect: PropTypes.string,
};
